import { Lead } from '@otw/models/leads/lead';
import { Vehicle } from '@otw/models/inventory/inventory';
import type { PickupLocation } from '@otw/models';
import { formatPrice } from 'core/utils/inventoryUtils';

export interface InvoiceRequestTextInput {
  lead: Lead;
  customerName: string;
  businessAddressLine: string;
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  vehicles: Vehicle[];
  getDropoffAddressLine: (vehicleId: string) => string;
}

export const formatLocationCommaLine = (
  location: PickupLocation | undefined | null,
): string => {
  if (!location) return '';
  const cityState = [location.city, location.state]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(' ');
  return [location.address?.trim(), cityState, location.zip?.trim()]
    .filter(Boolean)
    .join(', ');
};

const formatVehicleBlock = (
  vehicle: Vehicle,
  getDropoffAddressLine: (vehicleId: string) => string,
): string => {
  const stockLine = vehicle.stock_number?.trim()
    ? `Stock #${vehicle.stock_number.trim()}`
    : 'Stock #N/A';

  const yearMakeModel = [vehicle.year, vehicle.make, vehicle.model]
    .map((part) => (part != null ? String(part).trim() : ''))
    .filter(Boolean)
    .join(' ');

  const sizeLine = vehicle.size?.trim() || '';
  const vinLine = vehicle.vin?.trim()
    ? `VIN: ${vehicle.vin.trim()}`
    : 'VIN: N/A';

  const salePrice = formatPrice(vehicle.optic_list_price);
  const salePriceLine = salePrice
    ? `Sale Price: ${salePrice}`
    : 'Sale Price: N/A';

  const dropoffLine = getDropoffAddressLine(vehicle.id);
  const dropoffLocationLine = dropoffLine
    ? `Dropoff Location: ${dropoffLine}`
    : 'Dropoff Location: Not assigned';

  const lines = [
    stockLine,
    yearMakeModel,
    ...(sizeLine ? [sizeLine] : []),
    vinLine,
    salePriceLine,
    '',
    dropoffLocationLine,
  ];

  return lines.join('\n');
};

export const generateInvoiceRequestText = (input: InvoiceRequestTextInput): string => {
  const {
    lead,
    customerName,
    businessAddressLine,
    contactName,
    contactEmail,
    contactPhone,
    vehicles,
    getDropoffAddressLine,
  } = input;

  const leadIdentifier = lead.leadNumber?.trim() || lead.id;
  const vehicleBlocks = vehicles.map((vehicle) =>
    formatVehicleBlock(vehicle, getDropoffAddressLine),
  );

  const lines = [
    `INVOICE REQUEST LEAD #${leadIdentifier}`,
    '',
    'Customer:',
    customerName || 'N/A',
    '',
    'Business Address:',
    businessAddressLine || 'N/A',
    '',
    'Contact Information:',
    '',
    `Contact Name: ${contactName || 'N/A'}`,
    `Email: ${contactEmail || 'N/A'}`,
    `Phone: ${contactPhone || 'N/A'}`,
    '',
    'Vehicles:',
    '',
    ...vehicleBlocks.flatMap((block, index) =>
      index < vehicleBlocks.length - 1 ? [block, ''] : [block],
    ),
  ];

  return lines.join('\n');
};
